<template>
	<div class="ingame-completed">
		<div class="ingame-completed__header">
			<div class="ingame-completed__header__title">
				{{ $t('checkout.purchase-complete') }}
			</div>

			<div class="ingame-completed__header__message">
				{{ $t('checkout.purchase-completed-successfully') }}
			</div>
		</div>

		<div class="ingame-completed__details">
			<div class="order-details">
				<div v-if="orderNumber" class="order-details__row">
					<div class="order-details__row__title">{{ $t('checkout.order-number') }}</div>
					<div class="order-details__row__value">{{ orderNumber }}</div>
				</div>
				<div v-if="orderDate" class="order-details__row">
					<div class="order-details__row__title">{{ $t('checkout.order-date') }}</div>
					<div class="order-details__row__value">{{ orderDate }}</div>
				</div>
				<div v-if="paymentMethod && !isUseMileageAll" class="order-details__row">
					<div class="order-details__row__title">{{ $t('checkout.payment-method') }}</div>
					<div class="order-details__row__value">{{ paymentMethod }}</div>
				</div>
			</div>
		</div>

		<div class="ingame-completed__details-title">
			{{ $t('checkout.order-details') }}
		</div>

		<div class="ingame-completed__items">
			<div class="ingame-completed__items__item ingame-completed__items__item--title">
				<div class="ingame-completed__items__item__name">{{ $t('item') }}</div>
				<div class="ingame-completed__items__item__qty">{{ $t('checkout.qty') }}</div>
				<div class="ingame-completed__items__item__value">{{ $t('checkout.price') }}</div>
			</div>

			<div v-for="(item, index) in products" :key="index" class="ingame-completed__items__item">
				<div class="ingame-completed__items__item__name" :title="item.name">
					<span class="ingame-completed__items__item__name__title">
						{{ $t('item') }}
					</span>
					<span>{{ item.name }}</span>
				</div>
				<div class="ingame-completed__items__item__qty">
					<span class="ingame-completed__items__item__qty__title">
						{{ $t('checkout.qty') }}
					</span>
					<span>{{ item.quantity }}</span>
				</div>
				<div class="ingame-completed__items__item__value">
					<span class="ingame-completed__items__item__value__title">
						{{ $t('checkout.price') }}
					</span>
					<currency :code="item.currency" :value="item.amount" />
				</div>
			</div>
		</div>
		<div
			v-if="productPurchaseInfo"
			:class="
				hasPayment && (hasSalesTax || isUseMileage) && !isUseWallet
					? 'ingame-completed__total'
					: 'ingame-completed__total-large'
			"
		>
			{{ $t('checkout.order-total') }}
			<currency
				class="ingame-completed__total__value"
				:code="productPurchaseInfo.currency"
				:value="productPurchaseInfo.orderTotal"
			/>
		</div>

		<div v-if="hasSalesTax && !isUseWallet && !isUseMileageAll" class="ingame-completed__tax">
			{{ $t('checkout.sales-tax') }}
			<currency class="ingame-completed__total__value" :code="payment.currency" :value="payment.taxAmount" />
		</div>

		<div v-if="isUseMileage && !isUseWallet" class="ingame-completed__mileage">
			{{ $t('mileage.used-mileage', { currency: mileageName }) }}
			<span class="ingame-completed__total__value">{{ mileageUnit }}{{ mileageAmount }}</span>
		</div>

		<div v-if="(hasSalesTax || isUseMileage) && !isUseWallet" class="ingame-completed__amount-paid">
			{{ $t('checkout.amount-paid') }}
			<currency class="ingame-completed__total__value" :code="payment.currency" :value="paidAmount" />
		</div>

		<div v-if="paymentBalanceUpdate" class="ingame-completed__updates">
			<div v-if="paymentBalanceUpdateConsumption" class="ingame-completed__updates__withdrawn">
				<div class="ingame-completed__updates__withdrawn__title">
					{{ $t('checkout.debit-prior-balance') }}
				</div>
				<div class="ingame-completed__updates__withdrawn__value">
					<currency
						:code="paymentBalanceUpdateConsumption.currency"
						:value="paymentBalanceUpdateConsumption.amount"
					/>
				</div>
			</div>

			<div v-if="paymentBalanceUpdatePurchase" class="ingame-completed__updates__purchased">
				<div class="ingame-completed__updates__purchased__title">
					{{ $t('checkout.balance-purchased') }}
				</div>

				<div class="ingame-completed__updates__purchased__value">
					<currency
						:code="paymentBalanceUpdatePurchase.currency"
						:value="paymentBalanceUpdatePurchase.amount"
					/>

					<template v-if="payment && paymentBalanceUpdate && paymentBalanceUpdate.isNX">
						<currency
							class="ingame-completed__updates__purchased__value__amount"
							:code="payment.currency"
							:value="payment.amount"
						/>
					</template>
				</div>
			</div>
		</div>

		<div v-if="isDisplayBalance" class="ingame-completed__balance">
			{{ $t('checkout.balance-updated') }}
			<currency class="ingame-completed__balance__value" :code="totalBalanceCurrency" :value="totalBalance" />
		</div>

		<div v-if="!hasPayment || isUseWallet" class="ingame-completed__help">
			<div class="ingame-completed__help__balance">{{ $t('checkout.ingame.balance-update-moment') }}</div>
			<i18n class="ingame-completed__help__contacts" path="checkout.for-information-billing" tag="div">
				<template v-slot:nexonSupportCenter>
					<a :href="supportLink" target="_blank" @click="supportTracking">
						{{ $t('checkout.nexon-support-center') }}
					</a>
				</template>
			</i18n>
		</div>

		<div class="ingame-completed__actions">
			<FormButton
				class="btn-primary"
				:label="$t('checkout.return-to-game')"
				:button-click="handleClose"
				:disabled="addMoreFundsLoading"
			/>
		</div>
	</div>
</template>

<script>
import { FormButton } from '@arena/toolkit';
import resultMixin from './result-mixin';
import currency from '@/components/common/currency';

export default {
	name: 'InGamePurchasePending',
	components: {
		FormButton,
		currency,
	},
	mixins: [resultMixin],
	data() {
		return {
			addMoreFundsLoading: false,
		};
	},
	computed: {
		productPurchaseInfo() {
			return this.inGameTransactionResult.productPurchaseInfo;
		},
		products() {
			return this.productPurchaseInfo && this.productPurchaseInfo.products
				? this.productPurchaseInfo.products
				: [];
		},
		totalBalanceCurrency() {
			return (
				this.inGameTransactionResult.account &&
				this.inGameTransactionResult.account.balance &&
				this.inGameTransactionResult.account.balance.currency
			);
		},
		totalBalance() {
			return (
				this.inGameTransactionResult.account &&
				this.inGameTransactionResult.account.balance &&
				this.inGameTransactionResult.account.balance.total
			);
		},
		isDisplayBalance() {
			return (
				this.inGameTransactionResult.account &&
				this.inGameTransactionResult.account.balance &&
				this.inGameTransactionResult.account.balance.isDisplayBalance
			);
		},
		isUseWallet() {
			return this.payment && this.payment.isUseWallet;
		},
		hasSalesTax() {
			return this.payment && this.payment.hasSalesTax;
		},
		paymentBalanceUpdate() {
			return this.payment && this.payment.balanceUpdate;
		},
		paymentBalanceUpdateConsumption() {
			return this.paymentBalanceUpdate && this.paymentBalanceUpdate.consumption;
		},
		paymentBalanceUpdatePurchase() {
			return this.paymentBalanceUpdate && this.paymentBalanceUpdate.purchase;
		},
		paidAmount() {
			return this.payment.amount;
		},
		isUseMileage() {
			return this.payment && this.payment.isUseMileage && this.mileageAmount > 0;
		},
		mileageAmount() {
			return this.payment && this.payment.mileageAmount;
		},
		mileageName() {
			return (this.payment && this.payment.coinName) || '';
		},
		mileageUnit() {
			return (this.payment && this.payment.unitName) || '';
		},
		isUseMileageAll() {
			const orderTotal = (this.productPurchaseInfo && this.productPurchaseInfo.orderTotal) || 0;
			return this.isUseMileage && this.paidAmount === 0 && this.mileageAmount === orderTotal;
		},
	},
	mounted() {
		this.$a2s.view('purchase_completed_imp', {
			name: 'Payment_Method',
			value: this.paymentMethod,
		});
	},
	methods: {
		handleClose() {
			this.$a2s.click('purchase_completed_done');
			this.close();
		},
	},
};
</script>

<style lang="scss" scoped>
.ingame-completed {
	max-width: 640px;
	margin-left: auto;
	margin-right: auto;
	$balanceColor: #eda03b;
	$self: &;

	&__header {
		text-align: center;
		line-height: 1;
		padding-bottom: 16px;
		border-bottom: 2px solid rgba(#fff, 0.5);

		&:before {
			content: '';
			display: block;
			width: 100%;
			height: 100px;
			background-image: url('~@/images/purchase-complete.png');
			background-repeat: no-repeat;
			background-size: contain;
			background-position: center center;
			margin-top: 40px;

			@media (max-width: 700px) {
				content: none;
			}
		}
		&__title {
			font-size: 24px;
			padding-top: 13px;
			color: #fff;
		}

		&__message {
			padding-top: 10px;
			line-height: 1.3;
			max-width: 490px;
			margin: 0 auto;
		}
	}
	&__details {
		.order-details {
			&__row {
				display: flex;
				flex-wrap: wrap;
				justify-content: space-between;
				padding-top: 8px;
				line-height: 1.4;

				&:first-child {
					padding-top: 14px;
				}

				&:last-child {
					padding-bottom: 14px;
				}

				&__title,
				&__value {
					display: block;
					max-width: 100%;
				}
				&__title {
					margin-right: auto;
					padding-right: 15px;
				}
				&__value {
					color: #fff;
				}

				@media (max-width: 600px) {
					justify-content: start;
					&__title,
					&__value {
						flex: 0 0 100%;
						max-width: 100%;
					}
					&__value {
						word-break: break-word;
						word-wrap: break-word;
					}
				}
			}
		}
	}

	&__details-title {
		font-size: 18px;
		line-height: 1;
		font-weight: bold;
		color: #fff;
		padding-top: 15px;
	}

	&__items {
		margin-top: 16px;
		border-top: 2px solid #8f8f8f;
		color: #fff;

		&__item {
			$self: &;

			&#{$self}--title {
				display: none;
			}
		}
		@media (max-width: 699px) {
			&__item {
				font-size: 16px;
				line-height: 18px;
				border-bottom: 1px solid #383838;
				min-width: 100%;
				display: table;

				&:last-child {
					border-bottom: none;
				}

				&__name,
				&__qty,
				&__value {
					display: table-row;
					&__title {
						width: 1px;
						white-space: nowrap;
						padding-right: 10px;
					}
					> span {
						display: table-cell;
						padding-top: 5px;
						padding-bottom: 6px;
					}
				}

				&__name {
					> span {
						padding-top: 15px;
					}
				}

				&__value {
					> span {
						padding-bottom: 16px;
					}
				}
			}
		}
		@media (min-width: 700px) {
			display: table;
			font-size: 16px;
			line-height: 24px;
			min-width: 100%;
			padding-top: 10px;
			padding-bottom: 9px;

			&__item {
				display: table-row;
				$self: &;

				&#{$self}--title {
					display: table-row;
					font-size: 14px;
					line-height: 22px;
					font-weight: normal;
				}

				&__name,
				&__qty,
				&__value {
					display: table-cell;
					padding-top: 2px;
					padding-bottom: 4px;
					&__title {
						display: none;
					}
				}
				&__name {
					width: 1px;
					max-width: 250px;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					max-width: 424px;
				}
				&__qty {
					padding-left: 20px;
					padding-right: 10px;
					width: 1px;
					white-space: nowrap;
					text-align: center;
				}
				&__value {
					text-align: right;
					white-space: nowrap;
				}
			}
		}
	}

	&__total,
	&__total-large,
	&__mileage,
	&__tax {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		flex: 0 0 100%;
		max-width: 100%;
		&__value {
			margin-left: auto;
		}
	}
	&__total,
	&__tax {
		&__value {
			color: #fff;
		}
	}
	&__total {
		font-size: 14px;
		line-height: 1;
		padding-top: 16px;
		border-top: 1px solid rgba(#fff, 0.2);
	}

	&__total-large {
		font-size: 24px;
		line-height: 1;
		font-weight: bold;
		color: #fff;
		padding-top: 13px;
		border-top: 2px solid #8f8f8f;
	}
	&__tax {
		font-size: 14px;
		line-height: 1;
		padding-top: 14px;
	}

	&__mileage {
		font-size: 14px;
		line-height: 1;
		padding-top: 14px;
	}

	&__amount-paid {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		flex: 0 0 100%;
		max-width: 100%;
		font-size: 24px;
		line-height: 1;
		font-weight: bold;
		color: #fff;
		border-top: 2px solid rgba(#fff, 0.5);
		padding-top: 13px;
		margin-top: 18px;
	}

	&__updates {
		border-top: 1px solid rgba(#fff, 0.2);
		padding-top: 2px;
		padding-bottom: 2px;
		margin-top: 16px;

		&__withdrawn,
		&__purchased {
			display: flex;
			flex-wrap: nowrap;
			justify-content: space-between;
			padding-top: 14px;
			&__title {
				padding-right: 5px;
			}
			&__value {
				color: #fff;
				white-space: nowrap;
				text-align: right;
				&__amount {
					&:before {
						content: '/';
						padding: 0 4px;
					}
				}
			}
		}
	}

	&__balance {
		font-size: 16px;
		line-height: 20px;
		background-color: rgba(#000, 0.5);
		padding: 14px 20px 15px 20px;
		text-align: center;
		color: #fff;
		margin-top: 16px;
		&__value {
			color: $balanceColor;
			padding-left: 6px;
			font-weight: bold;
			@media (max-width: 600px) {
				display: block;
				padding-left: 0px;
			}
		}
	}
	&__help {
		text-align: center;
		padding-top: 34px;
		line-height: 1.3;
		a {
			text-decoration: underline;
			text-underline-position: under;
			text-transform: capitalize;
			color: inherit;
			&:hover {
				color: #fff;
			}
		}
		@media (max-width: 700px) {
			&__balance,
			&__contacts {
				display: inline;
			}
			&__contacts {
				padding-left: 2px;
			}
		}
	}
	&__actions {
		margin-top: 16px;
		button {
			width: auto;
			min-width: 320px;
			margin-top: 36px;
			margin-left: auto;
			margin-right: auto;

			@media (max-width: 700px) {
				min-width: 100%;
				width: 100%;
			}

			&.disabled {
				&:active {
					color: #262626;
					background-color: #fff;
					border-color: #d9d9d9;
				}
			}
			&.disabled.btn-primary {
				&:active,
				&:hover {
					color: #fff;
					background-color: #4d4d4d;
					border-color: #4d4d4d;
				}
			}
		}
		&:first-child {
			padding-left: 0px;
		}
		&:last-child {
			padding-right: 0px;
		}
	}
}
</style>
